/* ============================================================
   CUSTOMER REVIEWS — v106  (23 Sep 2026)

   Builds the photo review grid on a product page.

   WHERE THE DATA COMES FROM, AND WHY IT IS PARSED RATHER THAN READ
   Judge.me issues two API tokens. The PRIVATE one returns clean JSON
   (body, author, picture urls) but grants read/write to the whole
   account, so it can only ever live server-side — putting it in a theme
   asset publishes it to every visitor. The PUBLIC one is browser-safe
   and is what the widget API accepts, but that API answers with
   ready-made HTML rather than JSON.

   So: fetch their HTML with the public token, pull the three things we
   actually want out of it, and build our own cards.

   STARS: ONLY ON 5-STAR REVIEWS  (v117, Dev 30 Sep)
   v101-v116 never read the rating at all. Dev: "only on 5 stars show it,
   if someone gives lower don't show stars". So the score is read now, and
   a card gets five stars ONLY when the review is exactly 5. Any lower
   review still shows — same text, same photo — just without stars. There
   is deliberately no average anywhere, because an average would carry the
   lower scores into the page.

   PHOTOS KEEP THEIR OWN SHAPE  (v106)
   The cards sit in a two-column masonry (CSS columns, see
   product-reviews.css), so a card can be any height and the photo is
   simply shown at its own ratio. The old cut-to-4:5 frame, the crop
   threshold and the blurred backdrop are all gone with the strip they
   served. The one thing still decided per photo: a photo taller than
   3:5 (a full-length phone screenshot) gets the .rev-shot--tall cap so
   one card cannot be three screens high.

   SHOW MORE  (v106)
   The first `data-first` cards (6) show at once; the rest render hidden
   behind a "Show more reviews" button, like Voyage. The count in the
   meta line near the top of the page (data-revs-link) is filled here too.

   IT LIVES INSIDE THE COLOUR SWAP  (since v104)
   The section is inside .product-info, inside [data-product-root], whose
   innerHTML the colour swap replaces wholesale — so everything here looks
   its section up again on every load (live()) and listens for clicks on
   the document rather than on a node that may no longer be in the page.

   FAILS SAFE, ALWAYS
   No token, request refused, markup changed, zero reviews, one review
   with no photo while photos are required — every one of those paths
   ends with the section still hidden and the page exactly as it is
   today. Nothing half-rendered, and no empty "0 reviews" box.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-revs]');
  if (!root) return;

  var token = (root.getAttribute('data-token') || '').trim();
  var shop = (root.getAttribute('data-shop') || '').trim();
  var needPhoto = root.getAttribute('data-need-photo') === 'true';
  var maxCards = parseInt(root.getAttribute('data-max'), 10) || 12;
  var firstN = parseInt(root.getAttribute('data-first'), 10) || 6;
  if (!token || !shop) return;   /* not configured yet — stay invisible */

  var shown = '';                /* handle currently on screen */
  var busy = false;

  /* Taller than this (width / height) and the photo is capped at 3:5. */
  var TALL = 0.6;

  /* Star row styles live here rather than in product-reviews.css so the
     feature is one file. Ink colour, like every other mark on the page. */
  if (!document.getElementById('rev-stars-css')) {
    var st = document.createElement('style');
    st.id = 'rev-stars-css';
    st.textContent = '.rev-stars{margin:0 0 4px;font-size:13px;line-height:1;letter-spacing:2px;color:var(--ink,#14100D)}'
      /* Real columns (v119) — see COLUMNS below. */
      + '.revs-grid.is-cols{columns:auto;display:flex;align-items:flex-start;gap:10px}'
      + '.revs-grid.is-cols .revs-col{flex:1 1 0;min-width:0;display:flex;flex-direction:column;gap:10px}'
      + '.revs-grid.is-cols .rev{display:block;margin:0}'
      + '@media (min-width:761px){.revs-grid.is-cols,.revs-grid.is-cols .revs-col{gap:14px}}';
    document.head.appendChild(st);
  }

  /* The section currently in the page. See IT LIVES INSIDE THE COLOUR SWAP. */
  function live() {
    var r = document.querySelector('[data-revs]');
    if (r) root = r;
    return root;
  }

  function handleFromUrl() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  /* WHICH PHOTO ADDRESS  (v117, 30 Sep)
     Judge.me wraps each review photo in a link to the LARGE copy
     (href / data-mfp-src, width=1024) and puts a 160px THUMBNAIL on the img
     itself (data-src, width=160). v103-v116 took the thumbnail, so every card
     showed a 160px photo stretched to ~230px — soft on any phone. Now the
     link is read first and the width is set explicitly: 640 for the card,
     1024 for the full-photo viewer. The card still accepts whatever Judge.me
     sends if the markup ever changes (fallbacks below). */
  function withWidth(u, w) {
    if (!u) return '';
    if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, '$1' + w);
    if (u.indexOf('review-images.judgeme.com') === -1) return u;
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function imgSrc(img) {
    var a = img.closest ? img.closest('a') : null;
    var cands = [
      a && a.getAttribute('data-mfp-src'),
      a && a.getAttribute('href'),
      img.getAttribute('data-src'),
      img.getAttribute('src'),
      img.getAttribute('data-original')
    ];
    for (var i = 0; i < cands.length; i++) {
      var c = cands[i];
      if (c && c.indexOf('data:') !== 0 && /^https?:\/\//.test(c)) return c;
    }
    return '';
  }

  function pick(el, sels) {
    for (var i = 0; i < sels.length; i++) {
      var found = el.querySelector(sels[i]);
      if (found) return found;
    }
    return null;
  }

  function parse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var nodes = doc.querySelectorAll('.jdgm-rev, [data-review-id], .jdgm-review');
    var out = [];

    Array.prototype.forEach.call(nodes, function (n) {
      var body = text(pick(n, ['.jdgm-rev__body', '.jdgm-rev__content', '.jdgm-review__body']));
      if (!body) return;

      var author = text(pick(n, ['.jdgm-rev__author', '.jdgm-rev__buyer-name', '.jdgm-review__author']));
      var when = text(pick(n, ['.jdgm-rev__timestamp', '.jdgm-rev__time', '.jdgm-review__timestamp']));

      var pics = [];
      Array.prototype.forEach.call(
        n.querySelectorAll('.jdgm-rev__pic-img, .jdgm-rev__pics img, .jdgm-review__pics img, img.jdgm-rev__pic-img'),
        function (im) {
          var s = withWidth(imgSrc(im), 1024);
          if (s && pics.indexOf(s) === -1) pics.push(s);
        }
      );

      var rt = pick(n, ['.jdgm-rev__rating', '[data-score]']);
      var score = rt ? parseFloat(rt.getAttribute('data-score')) : NaN;

      out.push({ body: body, author: author, when: when, pics: pics, five: score === 5 });
    });

    return out;
  }

  function card(r, idx) {
    var hasPic = r.pics.length > 0;
    var who = [];
    if (r.author) who.push('<b>' + esc(r.author) + '</b>');
    if (r.when) who.push('<span>' + esc(r.when) + '</span>');

    var shot = '';
    if (hasPic) {
      /* No width/height attributes on purpose: the browser would use them
         as the aspect ratio before load, and the whole point is that the
         ratio is the photo's own. The card grows once the photo arrives. */
      shot =
        '<div class="rev-shot" data-rev-shot="' + idx + '" role="button" tabindex="0"' +
             ' aria-label="See this photo full size">' +
          '<img class="rev-photo" src="' + esc(withWidth(r.pics[0], 640)) + '"' +
              ' alt="Customer photo' + (r.author ? ' from ' + esc(r.author) : '') + '"' +
              ' decoding="async">' +   /* NOT loading=lazy: Safari can leave lazy images inside CSS columns unloaded forever (v117) */
          (r.pics.length > 1 ? '<span class="rev-more">+' + (r.pics.length - 1) + '</span>' : '') +
        '</div>';
    }

    return '<figure class="rev' + (hasPic ? '' : ' rev--noshot') + '"' + (idx >= firstN ? ' hidden' : '') + '>' +
      shot +
      '<figcaption class="rev-text">' +
        (r.five ? '<p class="rev-stars" role="img" aria-label="Rated 5 out of 5">&#9733;&#9733;&#9733;&#9733;&#9733;</p>' : '') +
        (who.length ? '<p class="rev-who">' + who.join('') + '</p>' : '') +
        '<p class="rev-body">' + esc(r.body) + '</p>' +
      '</figcaption>' +
    '</figure>';
  }

  /* Cap only the absurdly tall ones. naturalWidth is 0 while loading and
     stays 0 on failure; both correctly leave the photo alone. */
  function capTall(img) {
    var w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return;
    if (w / h < TALL) img.parentNode.classList.add('rev-shot--tall');
  }

  function armPhotos() {
    var rowEl = live().querySelector('[data-revs-row]');
    if (!rowEl) return;
    Array.prototype.forEach.call(rowEl.querySelectorAll('.rev-photo'), function (img) {
      if (img.complete) capTall(img);
      else img.addEventListener('load', function () { capTall(img); }, { once: true });
    });
  }

  /* ---------- full-photo viewer ---------- */
  var lb = null, lbImg = null, lbCount = null, lbPrev = null, lbNext = null;
  var lbShots = [], lbAt = 0, lbReturn = null;

  function buildViewer() {
    if (lb) return;
    lb = document.createElement('div');
    lb.className = 'revlb';
    lb.hidden = true;
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML =
      '<img class="revlb-img" alt="">' +
      '<button type="button" class="revlb-close" aria-label="Close">&times;</button>' +
      '<button type="button" class="revlb-nav revlb-prev" aria-label="Previous photo">&#8249;</button>' +
      '<button type="button" class="revlb-nav revlb-next" aria-label="Next photo">&#8250;</button>' +
      '<span class="revlb-count"></span>';
    document.body.appendChild(lb);

    lbImg = lb.querySelector('.revlb-img');
    lbCount = lb.querySelector('.revlb-count');
    lbPrev = lb.querySelector('.revlb-prev');
    lbNext = lb.querySelector('.revlb-next');

    lb.addEventListener('click', function (e) {
      if (e.target.closest('.revlb-close')) { closeViewer(); return; }
      if (e.target.closest('.revlb-prev')) { step(-1); return; }
      if (e.target.closest('.revlb-next')) { step(1); return; }
      /* Anywhere that is not the photo or a control closes it. */
      if (e.target === lb) closeViewer();
    });
  }

  function step(d) {
    if (!lbShots.length) return;
    lbAt = (lbAt + d + lbShots.length) % lbShots.length;
    lbImg.src = lbShots[lbAt];
    lbCount.textContent = lbShots.length > 1 ? (lbAt + 1) + ' / ' + lbShots.length : '';
  }

  function openViewer(shots, alt) {
    if (!shots || !shots.length) return;
    buildViewer();
    lbShots = shots;
    lbAt = -1;
    lbImg.alt = alt || '';
    lbPrev.hidden = shots.length < 2;
    lbNext.hidden = shots.length < 2;
    lbReturn = document.activeElement;
    lb.hidden = false;
    lb.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    step(1);

    /* The class drives the fade from opacity 0. requestAnimationFrame does
       NOT run in a backgrounded tab, and an overlay stuck at opacity 0 while
       still covering the page is a trap rather than a missed animation — so
       a timer backs it up. classList.add twice is a no-op. */
    requestAnimationFrame(function () { lb.classList.add('is-open'); });
    setTimeout(function () { lb.classList.add('is-open'); }, 60);

    lb.querySelector('.revlb-close').focus();
  }

  function closeViewer() {
    if (!lb || lb.hidden) return;
    lb.classList.remove('is-open');
    lb.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { lb.hidden = true; lbImg.removeAttribute('src'); }, 180);
    if (lbReturn && lbReturn.focus) lbReturn.focus();
    lbReturn = null;
  }

  var current = [];   /* the reviews currently rendered, for the viewer */

  document.addEventListener('click', function (e) {
    var more = e.target.closest && e.target.closest('[data-revs-more]');
    if (more) {
      var sec = live();
      Array.prototype.forEach.call(sec.querySelectorAll('.rev[hidden]'), function (f) { f.hidden = false; });
      more.hidden = true;
      return;
    }
    var shot = e.target.closest && e.target.closest('[data-rev-shot]');
    if (!shot) return;
    var r = current[parseInt(shot.getAttribute('data-rev-shot'), 10)];
    if (r) openViewer(r.pics, 'Customer photo' + (r.author ? ' from ' + r.author : ''));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (lb && !lb.hidden) return;
    var shot = e.target.closest && e.target.closest('[data-rev-shot]');
    if (!shot) return;
    e.preventDefault();
    shot.click();
  });

  document.addEventListener('keydown', function (e) {
    if (!lb || lb.hidden) return;
    if (e.key === 'Escape') closeViewer();
    else if (e.key === 'ArrowLeft') step(-1);
    else if (e.key === 'ArrowRight') step(1);
  });

  /* COLUMNS  (v119, 1 Oct)
     The grid used CSS multi-column (columns:2/3/4) and left the browser to
     balance the cards. With two equal cards on The Vixen it didn't: both
     stacked in the left column and the right one stayed empty. So the cards
     are now dealt into real flex columns here — card 1 left, card 2 right,
     card 3 left… — which keeps reading order and can never leave a column
     empty while there are cards to fill it. The count follows the same
     breakpoints the stylesheet used (2 phone / 3 tablet / 4 wide) and the
     grid is re-dealt when the width crosses one. */
  function colCount() {
    if (window.matchMedia('(min-width:1100px)').matches) return 4;
    if (window.matchMedia('(min-width:761px)').matches) return 3;
    return 2;
  }

  function deal(rowEl, html) {
    var n = colCount();
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var figs = Array.prototype.slice.call(tmp.children);
    rowEl.innerHTML = '';
    rowEl.classList.add('is-cols');
    rowEl.setAttribute('data-cols', n);
    var cols = [];
    for (var c = 0; c < n; c++) {
      var col = document.createElement('div');
      col.className = 'revs-col';
      rowEl.appendChild(col);
      cols.push(col);
    }
    figs.forEach(function (f, i) { cols[i % n].appendChild(f); });
  }

  var lastHtml = '';
  ['(min-width:761px)', '(min-width:1100px)'].forEach(function (q) {
    var mq = window.matchMedia(q);
    var redeal = function () {
      var rowEl = live().querySelector('[data-revs-row]');
      if (!rowEl || !lastHtml) return;
      /* keep cards the shopper already revealed with Show more */
      var open = !rowEl.querySelector('.rev[hidden]') && current.length > firstN;
      deal(rowEl, lastHtml);
      if (open) Array.prototype.forEach.call(rowEl.querySelectorAll('.rev[hidden]'), function (f) { f.hidden = false; });
      armPhotos();
    };
    if (mq.addEventListener) mq.addEventListener('change', redeal);
    else if (mq.addListener) mq.addListener(redeal);
  });

  function render(list) {
    var sec = live();
    var rowEl = sec.querySelector('[data-revs-row]');
    var countEl = sec.querySelector('[data-revs-count]');
    var moreEl = sec.querySelector('[data-revs-more]');
    var linkEl = document.querySelector('[data-revs-link]');
    if (!rowEl) return;
    if (needPhoto) {
      list = list.filter(function (r) { return r.pics.length > 0; });
    }
    list = list.slice(0, maxCards);

    if (!list.length) {          /* nothing worth showing — stay hidden */
      sec.hidden = true;
      if (linkEl) linkEl.hidden = true;
      return;
    }

    current = list;
    lastHtml = list.map(card).join('');
    deal(rowEl, lastHtml);
    armPhotos();
    var label = list.length === 1 ? '1 review' : list.length + ' reviews';
    if (countEl) countEl.textContent = label;
    if (moreEl) moreEl.hidden = list.length <= firstN;
    if (linkEl) { linkEl.textContent = label; linkEl.hidden = false; }
    sec.hidden = false;
  }

  function load() {
    var handle = handleFromUrl();
    var sec = live();
    /* A freshly swapped-in section is empty; re-render it even if the handle
       has not changed (Back to the same colour). */
    var fresh = sec && !sec.querySelector('.rev');
    if (!handle || busy || (handle === shown && !fresh)) return;
    busy = true;

    var url = 'https://judge.me/api/v1/widgets/product_review'
      + '?api_token=' + encodeURIComponent(token)
      + '&shop_domain=' + encodeURIComponent(shop)
      + '&handle=' + encodeURIComponent(handle)
      + '&per_page=' + encodeURIComponent(maxCards)
      /* FRESH EVERY 5 MINUTES  (v117). A Judge.me review is published with
         its TEXT first and its photo attached a moment later. A phone that
         loaded the page in between kept that text-only answer in its cache
         and went on showing a photo review as text (Dev's screenshot, 30
         Sep). The bucket changes every 5 minutes, so a phone asks again
         within 5 minutes at most, and repeat views inside the bucket still
         come from cache. */
      + '&v=' + Math.floor(Date.now() / 300000);

    fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('judgeme ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var html = data && (data.widget || data.html || '');
        if (!html) throw new Error('no widget html');
        shown = handle;
        render(parse(html));
      })
      .catch(function () {
        /* Anything at all goes wrong: leave the page as it was. */
        live().hidden = true;
        var linkEl = document.querySelector('[data-revs-link]');
        if (linkEl) linkEl.hidden = true;
      })
      .finally(function () { busy = false; });
  }

  load();

  /* ---------- follow the colour swap ----------
     main-product.liquid swaps colourways by replacing the innerHTML of
     [data-product-root] and pushing a new URL. This section is INSIDE that
     element, so a swap throws it away and drops in an empty, hidden copy;
     this watch is what fills the copy with the new colourway's reviews.
     childList on the root ONLY, not the subtree: the cards are rendered
     inside this element, so watching the subtree would make every render
     trigger another load. */
  var prod = document.querySelector('[data-product-root]');
  if (prod && 'MutationObserver' in window) {
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(load, 120);
    }).observe(prod, { childList: true });
  }
  window.addEventListener('popstate', function () { setTimeout(load, 120); });
})();
